import React from "react";
import { Reveal } from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";

/** Editorial two-column section: [image | text] or [text | image] */
export function EditorialSplit({
    eyebrow,
    number,
    title,
    body,
    image,
    imageAlt = "",
    reverse = false,
    accentBg = "bg-ivory",
}) {
    return (
        <section
            data-testid="editorial-split"
            className={`relative overflow-hidden py-20 md:py-28 ${accentBg}`}
        >
            <div className="relative mx-auto max-w-[1440px] px-6 md:px-12">
                <div
                    className={`grid lg:grid-cols-12 gap-12 lg:gap-20 items-center ${
                        reverse ? "lg:[&>*:first-child]:order-2" : ""
                    }`}
                >
                    <Reveal className="lg:col-span-6">
                        <div className="relative aspect-[4/5] md:aspect-[5/6] overflow-hidden rounded-[28px] shadow-editorial">
                            <img
                                src={image}
                                alt={imageAlt}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div
                                aria-hidden
                                className="absolute inset-0 bg-gradient-to-tr from-forest/10 to-transparent"
                            />
                        </div>
                    </Reveal>
                    <div className="lg:col-span-6">
                        <Reveal>
                            <div className="mb-5">
                                <SectionLabel number={number} text={eyebrow} />
                            </div>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="font-display text-[32px] md:text-[42px] leading-[1.08] text-ink">
                                {title}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <div className="mt-6 space-y-4 text-ink-soft text-[16px] leading-[1.75] max-w-xl">
                                {(Array.isArray(body) ? body : [body]).map(
                                    (p, i) => (
                                        <p key={i}>{p}</p>
                                    ),
                                )}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}

/** Chapter manifesto section: numbered heading + long body copy */
export function Manifesto({ number, eyebrow, title, body, accent = "bg-ivory" }) {
    return (
        <section className={`relative overflow-hidden py-20 md:py-28 ${accent}`}>
            <div className="relative mx-auto max-w-[1200px] px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-8 lg:gap-16">
                    <div className="lg:col-span-4">
                        <Reveal>
                            <div className="sticky top-40">
                                <SectionLabel number={number} text={eyebrow} />
                            </div>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-8">
                        <Reveal delay={0.1}>
                            <h2 className="font-display text-[32px] md:text-[44px] leading-[1.1] text-ink">
                                {title}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <div className="mt-8 space-y-5 text-ink-soft text-[16px] md:text-[17px] leading-[1.8] max-w-2xl">
                                {(Array.isArray(body) ? body : [body]).map(
                                    (p, i) => (
                                        <p key={i}>{p}</p>
                                    ),
                                )}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}

/** Grid of benefit cards */
export function BenefitsGrid({ eyebrow, title, subtitle, items = [], accent = "bg-blush/40" }) {
    return (
        <section className={`relative py-20 md:py-28 ${accent}`} data-testid="benefits-grid">
            <div className="mx-auto max-w-[1440px] px-6 md:px-12">
                <div className="max-w-3xl mb-12 md:mb-16">
                    {eyebrow && (
                        <Reveal>
                            <SectionLabel text={eyebrow} />
                        </Reveal>
                    )}
                    <Reveal delay={0.1}>
                        <h2 className="mt-5 font-display text-[32px] md:text-[44px] leading-[1.08] text-ink">
                            {title}
                        </h2>
                    </Reveal>
                    {subtitle && (
                        <Reveal delay={0.2}>
                            <p className="mt-5 text-ink-soft text-[16px] leading-relaxed max-w-2xl">
                                {subtitle}
                            </p>
                        </Reveal>
                    )}
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {items.map((it, i) => (
                        <Reveal key={i} delay={i * 0.06}>
                            <article className="group relative h-full bg-white rounded-[24px] p-7 md:p-8 shadow-soft hover:shadow-soft-lg transition-shadow duration-500 overflow-hidden">
                                <div
                                    aria-hidden
                                    className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-rose-powder/60 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                                />
                                <div className="relative">
                                    <SectionLabel
                                        number={String(i + 1).padStart(2, "0")}
                                        text=""
                                        tone="accent"
                                        className="!gap-2"
                                    />
                                    <h3 className="mt-4 font-display text-[22px] md:text-[26px] leading-[1.15] text-ink">
                                        {it.title}
                                    </h3>
                                    <p className="mt-4 text-ink-soft text-[15.5px] leading-relaxed">
                                        {it.body}
                                    </p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

/** Two callout cards side by side (When advised / When to consult) */
export function DualCallout({ eyebrow, title, subtitle, cards = [], accent = "bg-sage/20" }) {
    return (
        <section
            data-testid="dual-callout"
            className={`relative py-20 md:py-28 ${accent}`}
        >
            <div className="mx-auto max-w-[1200px] px-6 md:px-12">
                <div className="max-w-3xl mb-12">
                    {eyebrow && (
                        <Reveal>
                            <SectionLabel text={eyebrow} tone="sage" />
                        </Reveal>
                    )}
                    <Reveal delay={0.1}>
                        <h2 className="mt-5 font-display text-[30px] md:text-[40px] leading-[1.1] text-ink">
                            {title}
                        </h2>
                    </Reveal>
                    {subtitle && (
                        <Reveal delay={0.2}>
                            <p className="mt-5 text-ink-soft text-[16px] leading-relaxed">
                                {subtitle}
                            </p>
                        </Reveal>
                    )}
                </div>
                <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                    {cards.map((c, i) => (
                        <Reveal key={i} delay={i * 0.08}>
                            <article
                                className={`h-full rounded-[24px] p-7 md:p-9 shadow-soft ${
                                    i === 0
                                        ? "bg-rose-powder/70"
                                        : "bg-ivory-warm"
                                }`}
                            >
                                <SectionLabel
                                    text={i === 0 ? "Indicato" : "Nota"}
                                    tone="muted"
                                />
                                <h3 className="mt-4 font-display text-[24px] md:text-[28px] leading-[1.15] text-ink">
                                    {c.title}
                                </h3>
                                {c.body && (
                                    <p className="mt-4 text-ink-soft text-[15.5px] leading-relaxed">
                                        {c.body}
                                    </p>
                                )}
                                {c.list && (
                                    <ul className="mt-5 space-y-2.5 text-ink-soft text-[15.5px]">
                                        {c.list.map((l, j) => (
                                            <li
                                                key={j}
                                                className="flex items-start gap-3"
                                            >
                                                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-raspberry shrink-0" />
                                                <span>{l}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

/** Related pages footer */
export function RelatedLinks({ items = [] }) {
    return (
        <section className="py-16 md:py-20 bg-ivory">
            <div className="mx-auto max-w-[1200px] px-6 md:px-12">
                <div className="mb-8">
                    <SectionLabel text="Continua la scoperta" tone="muted" />
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((it) => (
                        <a
                            key={it.to}
                            href={it.to}
                            className="group flex items-baseline gap-4 p-5 rounded-2xl border border-line hover:border-raspberry hover:bg-rose-powder/30 transition-all"
                        >
                            <span className="text-raspberry text-lg">→</span>
                            <span className="font-display text-lg md:text-xl text-ink group-hover:text-raspberry transition-colors">
                                {it.label}
                            </span>
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
}
